import type { ApiClient } from '@acme/api-client'
import { inject, type InjectionKey } from 'vue'
import type * as T from './types'

type Id = string
type Opt = { signal?: AbortSignal }

// CNPJ analysis API (monitoramento-cnpj-api/src/CnpjMonitoringService.Api), at /api/analise-cnpj. Every route is per
// tenant: /tenants/{t}/cnpj/…
export function createCnpjApi(client: ApiClient) {
  const base = (tenantId: Id) => `tenants/${encodeURIComponent(tenantId)}/cnpj`
  const get = <R>(tenantId: Id, path: string, signal?: AbortSignal, query?: Record<string, string | number | boolean | undefined>) =>
    client.get<R>(`${base(tenantId)}/${path}`, { signal, query })
  const post = <R>(tenantId: Id, path: string, body?: unknown) => client.post<R>(`${base(tenantId)}/${path}`, body ?? {})
  const put = <R>(tenantId: Id, path: string, body?: unknown) => client.put<R>(`${base(tenantId)}/${path}`, body ?? {})

  return {
    // The caller's access (never 403: enabled=false when the analysis is off).
    access: (t: Id, o?: Opt) => get<T.MyAccessResponse>(t, 'access', o?.signal),

    // The institution's CNPJá key (only whether there is one and its last characters).
    apiKey: (t: Id, o?: Opt) => get<T.ApiKeyResponse>(t, 'api-key', o?.signal),
    saveApiKey: (t: Id, apiKey: string) => put<T.ApiKeyResponse>(t, 'api-key', { apiKey } satisfies T.UpdateApiKeyCommand),
    // Tests the typed key (not saved) or, empty, the saved one; reads the balance, no credit spent.
    testApiKey: (t: Id, apiKey?: string) => post<T.ApiKeyTestResponse>(t, 'api-key/test', { apiKey } satisfies T.TestApiKeyCommand),

    // Searches: queued here, run by the Worker; the detail is the progress or the network and its analysis.
    searches: (t: Id, query: { mine?: boolean; status?: T.SearchStatus; cnpj?: string; page?: number; pageSize?: number }, o?: Opt) =>
      get<T.SearchSummaryResponsePagedResponse>(t, 'searches', o?.signal, query),
    search: (t: Id, id: Id, o?: Opt) => get<T.SearchDetailResponse>(t, `searches/${id}`, o?.signal),
    createSearches: (t: Id, body: T.CreateSearchesCommand) => post<T.CreateSearchesResponse>(t, 'searches', body),
    redoSearch: (t: Id, id: Id, forceRefresh: boolean) => post<T.SearchSummaryResponse>(t, `searches/${id}/redo`, { forceRefresh } satisfies T.RedoSearchCommand),
    reanalyzeSearch: (t: Id, id: Id) => post<T.SearchDetailResponse>(t, `searches/${id}/reanalyze`),
    // Pause (queued: now; running: between two calls), continue from where it stopped, put first in the queue.
    pauseSearch: (t: Id, id: Id) => post<T.SearchSummaryResponse>(t, `searches/${id}/pause`),
    resumeSearch: (t: Id, id: Id) => post<T.SearchSummaryResponse>(t, `searches/${id}/resume`),
    prioritizeSearch: (t: Id, id: Id) => post<T.SearchSummaryResponse>(t, `searches/${id}/prioritize`),
    credit: (t: Id, o?: Opt) => get<T.CreditResponse>(t, 'credit', o?.signal),
    costs: (t: Id, mine: boolean, o?: Opt) => get<T.CostSummaryResponse>(t, 'costs', o?.signal, { mine }),

    // The current version of the risk rules (the same for every company).
    rules: (o?: Opt) => client.get<T.RuleSetResponse>('cnpj/rules', { signal: o?.signal }),

    // Role editor.
    permissionDefinitions: (o?: Opt) => client.get<T.PermissionDefinitionResponse[]>('cnpj/permissions', { signal: o?.signal }),
    getRolePermissions: (t: Id, role: string, o?: Opt) => get<T.RolePermissionsResponse>(t, `roles/${encodeURIComponent(role)}/permissions`, o?.signal),
    saveRolePermissions: (t: Id, role: string, permissions: string[]) =>
      put<T.RolePermissionsResponse>(t, `roles/${encodeURIComponent(role)}/permissions`, { permissions }),
  }
}

export type CnpjApi = ReturnType<typeof createCnpjApi>

export const CNPJ_API: InjectionKey<CnpjApi> = Symbol('CnpjApi')

export function useCnpjApi(): CnpjApi {
  const api = inject(CNPJ_API)
  if (!api) throw new Error('CNPJ_API was not provided.')
  return api
}

// Query keys: everything of one tenant's CNPJ analysis starts with ['cnpj', tenantId].
export const cnpjKeys = {
  all: (tenantId: Id) => ['cnpj', tenantId] as const,
  access: (tenantId: Id) => ['cnpj', tenantId, 'access'] as const,
  area: (tenantId: Id, area: string) => ['cnpj', tenantId, area] as const,
}
